import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import InfoIcon from "components/icons/infoIcon"
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import EmptyChart from 'views/components/emptyChart';
import i18n from 'i18n';
// Styles
import timeAvailableChartStyle from 'assets/jss/views/components/timeAvailableChartStyle'

import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';


//TODO If more than 12 columns in mobile display, break down in 2 sections with half the data each.
export default function TimeAvailableChart(props) {
    const { data } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const classes = timeAvailableChartStyle();
    const today = new Date();
    const monthNames = ["january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ];
    const [graphData, setGraphData] = useState([]);
    const chartMargin = {
        top: 10,
        right: 0,
        bottom: 50,
        left: 0
    };
    const [langDir, setLangDir] = useState(i18n.dir());
    const [format, setFormat] = useState(!localStorage.getItem('widgetTimeChart12or24hFormat') ? '12' : localStorage.getItem('widgetTimeChart12or24hFormat'));



    i18n.on('languageChanged', function (lng) {
        setLangDir(i18n.dir());
    })

    function getTimeZoneOffset(date, timeZone, timeformat12) {

        // Abuse the Intl API to get a local ISO 8601 string for a given time zone.
        let iso = date.toLocaleString('en-CA', {
            timeZone, hour12: false, hourCycle: 'h23'
        }).replace(', ', 'T').replace('T24', 'T00');

        // Include the milliseconds from the original timestamp
        iso += '.' + date.getMilliseconds().toString().padStart(3, '0');

        // Lie to the Date object constructor that it's a UTC time.
        const lie = new Date(iso + 'Z');

        // Return the difference in timestamps, as minutes
        // Positive values are West of GMT, opposite of ISO 8601
        // this matches the output of `Date.getTimeZoneOffset`
        return -(lie - date) / 60 / 60 / 1000;
    }

    useEffect(() => {
        if (Object.keys(data).length === 0) return;
        let facilityStartTime = data.facilityStartTime; // "10:20"
        let facilityEndTime = data.facilityEndTime;     // "20:30"
        if (!facilityStartTime || !facilityEndTime) return;
        let timezone = data.timezone;
        // 0 in UTC is midnight => America/Chicago is 
        // 1 in UTC is 1am...
        //
        let offset = getTimeZoneOffset(new Date(), timezone, format === '12'); //=> 5 ; hours diff. positive means behind, negative means over 
        //Start at StartTime: Get only first 2 digits
        let hourStart = parseInt(facilityStartTime.split(":")[0], 10);
        let hourEnd = parseInt(facilityEndTime.split(":")[0], 10);
        let currentDay = new Date().getDay();
        var newDataCompiled = [];
        let count = 0;
        for (var i = hourStart; i <= hourEnd; i++) {
            count++;
            if (count > 24) continue;
            let UTCtime = (i + offset) > 0 ? (i + offset) % 24 : (i + offset + 24) % 24;
            var newEntry = {};
            newEntry.time = i;
            if (format === '12') {
                newEntry.time = timeFrom24To12(i);
            }
            var dataToCheck = data.busyByHour[currentDay];
            var percentage = dataToCheck[UTCtime].unavailable;
            newEntry.unavailable = percentage;

            newDataCompiled.push(newEntry);
        }
        if (langDir === "rtl")
            newDataCompiled = newDataCompiled.reverse();
        setGraphData(newDataCompiled);
    }, [data, langDir, format]);

    function timeFrom24To12(localTime) {
        if (localTime === 12) return "12pm";
        if (localTime === 24) return "12am";
        if (localTime === 0) return "12am";
        let newTime = localTime % 12;
        let newSuffix = localTime > 12 ? "pm" : "am";
        return newTime + newSuffix;
    }

    const handleFormat = (event, newFormat) => {
        if (newFormat !== null) {
            setFormat(newFormat);
            localStorage.setItem('widgetTimeChart12or24hFormat', newFormat);
        }
    };

    return (
        <div className={classes.container}>
            <h3 className={classes.title}>
                <strong className={classes.titleMargin}><Trans i18nKey="graphTitle" /></strong>
                <Tooltip
                    disableFocusListener
                    enterTouchDelay={100}
                    leaveTouchDelay={4500}
                    id="instagram-tooltip"
                    title={<Trans i18nKey="graphMoreInfoMessage" />}
                    placement={"bottom"}
                    classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                    arrow
                >
                    <Button className={classes.buttonTooltip}>
                        <InfoIcon className={classes.buttonColor} />
                    </Button>
                </Tooltip>
            </h3>
            <div className={classes.graphSubrow}>
                <div className={classes.dateDiv}>{t('graphToday') + ', ' + t(monthNames[today.getMonth()]) + ' ' + today.getDate() + ', ' + today.getFullYear()}</div>
                <div className={classes.dateFormatSwitch}>
                    <ToggleButtonGroup
                        value={format}
                        onChange={handleFormat}
                        exclusive
                        aria-label="device">
                        <ToggleButton value="12" aria-label="12h" color="primary">
                            <span>12h</span>
                        </ToggleButton>
                        <ToggleButton value="24" aria-label="24h">
                            <span >24h</span>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>
            {graphData.length > 0 ?
                <ResponsiveBar
                    height={300}
                    data={graphData}
                    keys={['unavailable']}
                    indexBy="time"
                    margin={chartMargin}
                    padding={0.3}
                    colors={theme.palette.primary.main}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    axisTop={null}
                    axisRight={null}
                    minValue={0}
                    maxValue={100}
                    axisBottom={{
                        tickSize: 15,
                        tickPadding: 5,
                        tickRotation: -90,
                    }}
                    axisLeft={null}
                    enableGridX={false}
                    gridYValues={[0, 25, 50, 75, 100]}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    enableLabel={false}
                    animate={true}
                    motionStiffness={90}
                    motionDamping={15}
                    tooltip={({ id, value, indexValue, color }) => (
                        <strong style={{ color }}>
                            Occupancy - {indexValue}: {(Math.round(value * 100) / 100).toFixed(0)}%
                        </strong>
                    )}
                /> :
                <EmptyChart />
            }
        </div >
    );
}


TimeAvailableChart.propTypes = {
    data: PropTypes.object,
};
